// client/src/contexts/AuthContext.tsx
// Firebase Auth state management for AGOC School.
//
// Platform auth model:
//   - Firebase Auth project: agoc-platform-dev (NOT icaros-agoc-school)
//   - After sign-in, the ID token carries JWT custom claims:
//       tenantId (string)  — set by platform POST /admin/users/:uid/claims
//       roles    (string[]) — e.g. ['student'] | ['teacher'] | ['admin']
//   - Custom claims appear only after a token refresh (force-refresh on login)
//   - Role-based routing is driven entirely from claims — no Firestore user doc read
//
// Differences from the legacy PilotAcademyAI AuthContext:
//   - No role read from Firestore 'users' collection
//   - No session-restoration hack for admin user creation
//   - tenantId comes from token, not from a subdomain or header on the client
//   - Exposes roles as string[] (not a union type) to stay flexible across tenants
//
// forceRefresh strategy:
//   - Cold load (onAuthStateChanged on page open): forceRefresh=false — reads cached token,
//     no network call unless token is already expired. Fast.
//   - Explicit sign-in (Google / email): forceRefresh=true — forces a token refresh so
//     newly-set custom claims (roles, tenantId) are available immediately.
//   - An isSigningInRef flag is set by login methods BEFORE signIn is called.
//     onAuthStateChanged reads and clears the flag to decide forceRefresh.
//     This prevents duplicate resolveUser calls (once from login method, once from listener).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { IdTokenResult, User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, signInWithGoogle, signInWithEmail, signOut } from '@/lib/firebase';
import { ensureMyProfile } from '@/services/userService';

// ── Types ────────────────────────────────────────────────────────────────────

/** The platform-normalised user object exposed by the AuthContext. */
export interface PlatformUser {
  /** Firebase UID. */
  uid: string;

  /** Email from Firebase Auth. */
  email: string | null;

  /** Display name from Firebase Auth profile. */
  displayName: string | null;

  /** Photo URL from Firebase Auth profile (Google sign-in). */
  photoURL: string | null;

  /**
   * Tenant this user belongs to.
   * Sourced from the `tenantId` JWT custom claim.
   * Empty string if the claim is not yet set (user was just created by admin).
   */
  tenantId: string;

  /**
   * List of role strings from the `roles` JWT custom claim.
   * Examples: ['student'], ['teacher'], ['admin'], ['teacher_admin', 'admin']
   * Empty array if no roles claim is present.
   */
  roles: string[];
}

interface AuthContextValue {
  /** The platform-normalised user. Null if not authenticated. */
  user: PlatformUser | null;

  /** Raw Firebase user object — available for any Firebase SDK calls. */
  firebaseUser: FirebaseUser | null;

  /** True while Firebase resolves the initial auth state. */
  loading: boolean;

  /** Error string if the last auth operation failed. */
  error: string | null;

  /** True if the user is authenticated but has no roles assigned yet. */
  needsRoleAssignment: boolean;

  /** Sign in with Google OAuth popup. */
  loginWithGoogle: () => Promise<void>;

  /** Sign in with email and password. */
  loginWithEmail: (email: string, password: string) => Promise<void>;

  /** Sign out and clear state. */
  logout: () => Promise<void>;

  /** Force-refresh the ID token (use after admin sets new claims). */
  refreshToken: () => Promise<void>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Max wait for token/claims so auth loading does not hang indefinitely. */
const CLAIMS_TIMEOUT_MS = 25_000;

function claimsFromTokenResult(tokenResult: IdTokenResult): {
  tenantId: string;
  roles: string[];
} {
  const claims = tokenResult.claims as Record<string, unknown>;
  const tenantId = typeof claims['tenantId'] === 'string' ? claims['tenantId'] : '';
  const roles = Array.isArray(claims['roles'])
    ? (claims['roles'] as unknown[]).filter((r): r is string => typeof r === 'string')
    : [];
  return { tenantId, roles };
}

/**
 * Decode JWT custom claims from the Firebase ID token result.
 *
 * forceRefresh=false (default): reads the locally cached token — no network call unless
 * the token is already expired. Safe to use on every onAuthStateChanged.
 *
 * forceRefresh=true: forces a network round-trip to Firebase to get fresh claims.
 * Only needed immediately after sign-in or after an admin has updated the user's claims.
 *
 * Wrapped in a timeout so a slow/failing network does not leave the app stuck on "Cargando...".
 * Timeout is cleared when the token returns first (avoids a spurious warning 10–25s later).
 */
async function extractClaims(
  firebaseUser: FirebaseUser,
  forceRefresh = false,
): Promise<{ tenantId: string; roles: string[] }> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        console.warn('[AuthContext] getIdTokenResult timed out — check network / Firebase');
        reject(new Error('Auth claims timeout — check network or try again'));
      }, CLAIMS_TIMEOUT_MS);
    });
    const tokenResult = await Promise.race([
      firebaseUser.getIdTokenResult(forceRefresh),
      timeoutPromise,
    ]);
    return claimsFromTokenResult(tokenResult as IdTokenResult);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

function toPlatformUser(
  firebaseUser: FirebaseUser,
  claims: { tenantId: string; roles: string[] },
): PlatformUser {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    tenantId: claims.tenantId,
    roles: claims.roles,
  };
}

// ── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set to true by login methods before calling Firebase signIn.
  // onAuthStateChanged reads this flag to decide whether to force-refresh the token.
  // Prevents duplicate resolveUser calls (login method + listener firing simultaneously).
  const isSigningInRef = useRef(false);

  // Tracks which uid already triggered a Firestore profile ensure this session.
  const ensuredProfileUidRef = useRef<string | null>(null);

  // ── Internal: resolve Firebase user → PlatformUser ──────────────────────
  // forceRefresh=false: use cached token (safe for onAuthStateChanged on cold load).
  // forceRefresh=true: force token refresh (use after sign-in or admin claim update).
  const resolveUser = useCallback(async (fbUser: FirebaseUser | null, forceRefresh = false) => {
    if (!fbUser) {
      setFirebaseUser(null);
      setUser(null);
      ensuredProfileUidRef.current = null;
      setLoading(false);
      return;
    }

    // Mark loading while we fetch/refresh the token — prevents an intermediate render
    // where firebaseUser and user are both null while the network call is in-flight.
    setLoading(true);

    try {
      const claims = await extractClaims(fbUser, forceRefresh);
      setFirebaseUser(fbUser);
      setUser(toPlatformUser(fbUser, claims));
      console.log(
        '[AuthContext] User resolved uid=%s tenantId=%s roles=%s',
        fbUser.uid,
        claims.tenantId,
        claims.roles.join(','),
      );

      // Ensure Firestore profile exists (legacy users, or partial admin creates).
      // GET /users/me auto-creates tenants/{tenantId}/users/{uid} server-side.
      if (claims.tenantId && claims.roles.length > 0 && ensuredProfileUidRef.current !== fbUser.uid) {
        ensuredProfileUidRef.current = fbUser.uid;
        void ensureMyProfile().catch((err) => {
          console.warn('[AuthContext] ensureMyProfile failed:', err);
          ensuredProfileUidRef.current = null;
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to resolve user claims';
      console.error('[AuthContext] Failed to extract claims:', message);
      setError(message);
      // Still set the firebase user so the UI can show a partial state
      setFirebaseUser(fbUser);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Subscribe to Firebase Auth state ─────────────────────────────────────
  // forceRefresh is true only when a login method just ran (isSigningInRef flag).
  // Cold-load path always uses forceRefresh=false to avoid a network call.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      const shouldForceRefresh = isSigningInRef.current;
      isSigningInRef.current = false; // always clear after reading
      void resolveUser(fbUser, shouldForceRefresh);
    });
    return unsubscribe;
  }, [resolveUser]);

  // ── Auth operations ───────────────────────────────────────────────────────
  // Login methods set isSigningInRef BEFORE calling Firebase so that when
  // onAuthStateChanged fires it uses forceRefresh=true. The methods do NOT
  // call resolveUser themselves — the listener handles it exclusively.

  const loginWithGoogle = useCallback(async () => {
    setError(null);
    isSigningInRef.current = true;
    try {
      await signInWithGoogle();
      // onAuthStateChanged will fire with forceRefresh=true and resolve the user.
    } catch (err) {
      isSigningInRef.current = false; // reset on error so listener uses false on next cold load
      const message = err instanceof Error ? err.message : 'Google sign-in failed';
      setError(message);
      throw err;
    }
  }, []);

  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      setError(null);
      isSigningInRef.current = true;
      try {
        await signInWithEmail(email, password);
        // onAuthStateChanged will fire with forceRefresh=true and resolve the user.
      } catch (err) {
        isSigningInRef.current = false; // reset on error
        const message = err instanceof Error ? err.message : 'Email sign-in failed';
        setError(message);
        throw err;
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    setError(null);
    await signOut();
    setUser(null);
    setFirebaseUser(null);
  }, []);

  const refreshToken = useCallback(async () => {
    if (!firebaseUser) return;
    setError(null);
    try {
      // Force-refresh so newly-set custom claims (tenantId, roles) are picked up.
      await resolveUser(firebaseUser, true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Token refresh failed';
      setError(message);
    }
  }, [firebaseUser, resolveUser]);

  // ── Derived state ─────────────────────────────────────────────────────────
  // A user needs role assignment if they are authenticated but have no roles claim yet
  const needsRoleAssignment =
    firebaseUser !== null && user !== null && user.roles.length === 0;

  const value: AuthContextValue = {
    user,
    firebaseUser,
    loading,
    error,
    needsRoleAssignment,
    loginWithGoogle,
    loginWithEmail,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook — must be used inside AuthProvider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('[useAuth] Must be used inside <AuthProvider>');
  }
  return ctx;
}
